/**
 * B1–B7 — the tool page shell. One module, sixteen pages.
 *
 * Nothing in this file knows what any tool does. It reads a per-tool config the
 * server rendered into the page (slug, ops, options schema, write formats) and
 * drives the same six-step skeleton every time: dropzone → parsed preview →
 * that tool's options → result → download. That is what makes "any two tool
 * pages differ only by config" a structural fact rather than a claim.
 *
 * Everything here runs in the browser. The one exception is B6 — an uploaded
 * audio file, which has no subtitle track to read and routes to transcription —
 * and it says so before anything runs, not after.
 *
 * ── DOCUMENTS LIVE IN THE WORKER ──────────────────────────────────────────
 * This module holds a `DocumentId` and a window of rows, never a document.
 * CORE measured a 40,000-cue `structuredClone` at 1.2–2.5 s on a desktop
 * against an interaction budget of about that; the cue table therefore asks
 * `lib/cue-window.ts` for the rows it is about to paint and nothing more.
 */

import type {
  DocumentId,
  Framerate,
  Op,
  Ticks,
  WriteFormat,
} from '../../../../../contracts/cue-model.ts';
import {
  cueNeeded,
  labelled,
  opsFromValues,
  probeInputs,
  type RunInputs,
  runShape,
} from '../../../../../src/tools/ops-from-values.ts';
import {
  type CueSpeed,
  cueSpeeds,
  formatCps,
  hasQcControls,
  qcProfileFollowers,
  qcSettingsFromValues,
} from '../../../../../src/tools/qc-from-values.ts';
import { bitmapLabel } from '../../../../../src/formats/classify.ts';
import { cueTimings } from '../../../../../src/tools/autosync.ts';
import { pickTarget, pointPairs } from '../../../../../src/tools/point-pick.ts';
import { saveAsChoices, writeFormatFor } from '../../../../../src/tools/write-format.ts';
import type {
  BatchItemStatus,
  CuePreviewRow,
  ExtractedTrackInfo,
  ParsedInfo,
} from '../../../../../src/core/protocol.ts';
import { mountAutosync } from '../lib/autosync.ts';
import { watchCopies } from '../lib/copies.ts';
import { type CueRow, type CueTableSource, mountCueTable } from '../lib/cue-table.ts';
import { createCueWindow } from '../lib/cue-window.ts';
import {
  type QcRow,
  type QcView,
  qcSentence,
  qcView,
  diffRows as rowsOfDiff,
  diffSummary as diffSummaryOf,
} from '../lib/diff-source.ts';
import { type DiffRow, mountDiffView } from '../lib/diff-view.ts';
import { must, on, qs, setData } from '../lib/dom.ts';
import {
  type DownloadChoice,
  downloadChoices,
  mayDownload,
  renderOutcome,
} from '../lib/download-row.ts';
import { partName, saveBytes } from '../lib/download.ts';
import {
  type DocumentKind,
  type DocumentRead,
  type DroppedDocument,
  isDocumentName,
  readDocument,
  sniffDocument,
} from '../lib/documents.ts';
import { mountDocxLayout } from '../lib/docx.ts';
import { ensurePdfFonts, PdfFontLoadError } from '../lib/pdf-fonts.ts';
import { mountPdfLayout } from '../lib/pdf-layout.ts';
import { withDownload } from '../lib/download-pref.ts';
import { pickedLang, pickedPlatform, withThen } from '../lib/hand-off.ts';
import { classify, extensionOf, mountDropzone, videoTypeName } from '../lib/dropzone.ts';
import { track } from '../lib/events.ts';
import { t } from '../lib/i18n.ts';
import { fileSize, shortFilename } from '../lib/format.ts';
import { mountOptionPanel, parseFramerate } from '../lib/option-panel.ts';
import { loadPinyinTable } from '../lib/pinyin.ts';
import {
  type OptionsSchema,
  type OptionValue,
  type OptionValues,
  walkFields,
} from '../lib/options-schema.ts';
import { loadPlatforms } from '../lib/platforms.ts';
import { awaitedFields, isRefusal, refusalShown } from '../lib/refusal.ts';
import { labelWithEncoding, mountSaveEncoding, TEXT_SAMPLE_CUES } from '../lib/save-encoding.ts';
import { renderState, showState } from '../lib/states.ts';
import { createToolSource, type ToolRun } from '../lib/tool-source.ts';
import { mountTranslatePair, pairSource } from '../lib/translate-pair.ts';
import { translateTarget, translateTargetName, withAsked } from '../lib/translate-target.ts';
import {
  buildId,
  type ImageDrop,
  type ImageSubtitleSource,
  isOcrPage,
  loadOcr,
  mayHoldImageSubtitles,
  OCR_PAGES,
  ocrHandoverHandler,
  readImageDrop,
  registerImageSubtitleHandler,
  sourceOf,
  takeHandedOver,
} from '../lib/image-subtitles.ts';
import { within } from '../lib/upload.ts';
import { createUploadOffer } from '../lib/upload-offer.ts';
import { mountMicPanel } from '../lib/mic-panel.ts';
import { failedFormat, resultReached, usageCounter } from '../lib/usage.ts';
import { mountStlRate, mountTtmlExtension } from '../lib/tv-formats.ts';
import { createVideoFlow } from '../lib/video-flow.ts';
import { downloadWriteOptions, writeFramerate } from '../lib/write-options.ts';
import { isVersionMismatch, WorkerFailure, worker } from '../lib/worker.ts';

/** A size WARNING over ~10 MB is honest; a size CAP is not, and there is none. */
const LARGE_FILE_BYTES = 10 * 1024 * 1024;

/**
 * The four fields `tool_context` actually puts in `tool`, and no others.
 *
 * ⚠ THIS TYPE IS ONE HALF OF A SHAPE WHOSE OTHER HALF IS PYTHON. `tool.html`
 * emits `{{ tool | tojson }}`, so nothing checks that the two agree, and an
 * optional field here reads to TypeScript as one the server *sometimes* sends.
 *
 * It did not sometimes send them. `write_format` and `result_view` were both
 * declared optional here and could never arrive: `_REQUIRED` in
 * `server/app/tools.py` does not list them, `parse()` REJECTS unknown fields so
 * no producer could add them, and `tool_context` emits `write_formats` (plural,
 * top level) rather than `write_format` (singular, nested). Two independent
 * mismatches stacked, so fixing only the plural would not have helped.
 *
 * The cost was that every branch guarded by them was dead — including the
 * ask-before-writing path this module builds — while typechecking, linting and
 * passing every test. `TOOL_CONTEXT_FIELDS` below is asserted against a real
 * rendered page, so the next field to go missing fails here instead.
 */
export interface ToolConfig {
  readonly slug: string;
  readonly label: string;
  readonly options_schema: OptionsSchema;
}

/**
 * What the runtime reads out of the page, beside `tool`.
 *
 * `write_formats` and `read_formats` are TOP-LEVEL context keys, not members of
 * `tool` — which is exactly the mismatch that killed the download branch.
 */
export interface ToolPageContext {
  readonly tool: ToolConfig;
  readonly read_formats: readonly string[];
  readonly write_formats: readonly WriteFormat[];
  /**
   * The op PIPELINE, top level beside the other list-shaped facts.
   *
   * A list because a list expresses zero, one and many with no special case: a
   * conversion is a format change and runs no op at all; `shift` is one;
   * `splitDocument` then `renumber` is two.
   *
   * ⚠ OPTIONAL IN THE TYPE, REQUIRED IN THE CONTRACT — and the gap between
   * those two sentences is the whole point. `api.md` 1.7.0 says every row
   * declares `ops`, so absent means THE COURIER IS BROKEN, not "this tool has
   * no ops". `readConfig` refuses in that case. It is typed optional because
   * JSON that crossed a template boundary is only as present as the sender made
   * it, and a type that promised otherwise would make the check look redundant.
   *
   *   absent            → the page refuses; the tool cannot run
   *   present and empty → a conversion; run the write with no pipeline
   *
   * Collapsing those two is how the defect this replaced survived.
   */
  readonly ops?: readonly string[];
  /** Pair pages only. */
  readonly from_format?: string;
  readonly to_format?: string;
  /**
   * SEO_BRIEF_04's video pages only: `video` on /extract-subtitles, `media` on /mp4-to-srt and
   * /srt-generator, whose job is transcription, so a dropped video is offered that first
   * (lib/video-flow.ts). Absent everywhere else.
   */
  readonly input?: string;
  /**
   * The audio pages (/audio-to-text and its siblings) and the video pages that transcribe:
   * the transcript download the visitor came for, `txt` or `srt`. Carried onto the job URL as
   * `?download=` (lib/download-pref.ts) so the transcript page puts it first.
   * Absent on every other page, whose uploads land on the default order.
   */
  readonly transcript_format?: string;
  /**
   * The language pages only (/spanish-transcription and its siblings, SEO_BRIEF_06 3.1): the
   * Whisper code the upload sends, so the model is told the language instead of detecting it.
   * Absent everywhere else, whose uploads send no language.
   */
  readonly language?: string;
  /**
   * The translation pages only (SEO_BRIEF_09 5): the language the speech is translated into,
   * the target picker's default. The upload sends the picker's value (lib/translate-target.ts).
   */
  readonly translate_to?: string;
  /**
   * The summarizers and the caption generator only (SEO_BRIEF_09 6.1, 6.1b): what the transcript
   * page does once the transcript is there, `summarize` or `captions`, handed on as `?do=` with
   * the upload (lib/hand-off.ts). Absent everywhere else.
   */
  readonly then?: string;
}

/** The exact key set `tool_context` builds. Pinned by a seam test. */
export const TOOL_CONTEXT_FIELDS = ['slug', 'label', 'options_schema'] as const;

/**
 * The op list to send, built from what the user actually set.
 *
 * ⚠ THIS USED TO BE `{ op, ...values }` ON THE FIRST OP AND `{ op }` ON THE
 * REST, and that was not a bug in the mapping — there was no mapping. The
 * option keys are namespaced by op (`stripFormatting.keepItalic`) and no `Op`
 * has a field by that name, so ops[0] received keys it could not read and every
 * later op received nothing at all: no parameters, no `where`. Worse,
 * `stripFormatting.enabled: false` — "leave my formatting alone" — landed on
 * the NORMALIZE op, so the strip ran anyway.
 *
 * Ten of the fifteen tool pages ignored every control, and the download still
 * produced a plausible file. Proven by running the page's own code path against
 * the real registry, not by reading it.
 *
 * `opsFromValues` is that mapping. It lives in `src/tools/` beside the engines
 * because a control surface and an `Op` are different vocabularies — `shift`
 * asks for a delta and a switch and produces `ranges` — and only the side that
 * owns the op knows the translation.
 *
 * An empty result stays legitimate: a conversion is a format change with no op
 * at all, and `apply` with no ops leaves the document exactly as parsed.
 *
 * `inputs` is what the files supply and no control can: the other documents of
 * a merge, and the start of the subtitle a split is cut before.
 */
function opsToApply(
  config: ToolPageContext,
  values: OptionValues,
  inputs: RunInputs = {},
): readonly Op[] {
  return opsFromValues(config.ops ?? [], values as Readonly<Record<string, unknown>>, inputs).ops;
}

/** Two framerate option values naming the same rational rate. */
function sameRate(a: OptionValue, b: OptionValue): boolean {
  const rate = (v: OptionValue) =>
    typeof v === 'object' && v !== null && !Array.isArray(v)
      ? (v as { num?: unknown; den?: unknown })
      : null;
  const x = rate(a);
  const y = rate(b);
  if (x === null || y === null) return false;
  const { num: n1, den: d1 } = x;
  const { num: n2, den: d2 } = y;
  if (typeof n1 !== 'number' || typeof d1 !== 'number') return false;
  if (typeof n2 !== 'number' || typeof d2 !== 'number') return false;
  return n1 * d2 === n2 * d1;
}

/**
 * The MicroDVD FPS prompt: a parse that needs a frame rate the file does not
 * state. A question for the user, not a failure, so the file is not counted
 * until the answer parses it.
 */
function asksForRate(err: unknown): boolean {
  return (
    err instanceof WorkerFailure &&
    (err.reason === 'needs-framerate' || err.reason === 'framerate-required')
  );
}

/**
 * What B5's "This looks like …" calls a file this page cannot read. The worker names a bitmap it
 * found by its bytes (a VobSub `.sub`) on the failure; a `.sup` or `.idx` never reaches it, being
 * refused by extension, so the extension names those. The words are `bitmapLabel`'s, the one
 * place they live, and anything else keeps the template's own.
 */
function unreadableKind(fileName: string, detail: unknown = null): Record<string, string> {
  const named = (detail as { kind?: unknown } | null)?.kind;
  if (typeof named === 'string' && named !== '') return { kind: named };
  const ext = extensionOf(fileName);
  if (ext === 'idx') return { kind: bitmapLabel('vobsub-idx') };
  if (ext === 'sup') return { kind: bitmapLabel('pgs-sup') };
  return {};
}

function readConfig(): ToolPageContext | null {
  const node = qs<HTMLScriptElement>('script[data-tool-config]');
  if (!node?.textContent) return null;
  try {
    const parsed = JSON.parse(node.textContent) as ToolPageContext;
    if (!parsed?.tool?.slug) {
      // A page whose config is the wrong SHAPE is not a page with a missing
      // option — it is a seam that moved, and the whole shell is about to run
      // against fields that are all undefined. Saying so beats six silent
      // no-ops.
      //
      // This checked `tool.op` until 1.7.0 removed it. `slug` replaces it as
      // the canary because it is the one key on this object that is never
      // meaningfully empty.
      console.error('[tool] tool config has no tool.slug — the page context shape changed', parsed);
      return null;
    }
    return parsed;
  } catch (err) {
    console.error('[tool] tool config is not valid JSON', err);
    return null;
  }
}

/**
 * The counts-only fallback that used to live here is GONE.
 *
 * The worker answered `diff` with a counts-only object that its own declared
 * `ResultTypes['diff']` forbade, because CLEAN's `diffDoc` was not registered
 * in this build — so a caller reading `.modified` got `undefined` at runtime
 * with nothing to catch it. C11 made that a TYPED FAILURE instead, and
 * `registrations.ts` now imports `../diff/index.ts`, so `diff` either returns a
 * real `DocDiff` or fails loudly.
 *
 * Keeping the union would mean carrying a branch for a shape the worker can no
 * longer produce, and that branch is where a real `DocDiff` would silently be
 * mistaken for the degraded one — every `DocDiff` has `modifiedCount` too.
 */

/**
 * Take the tool off the page, rather than leaving a dropzone above an error.
 *
 * A file dropped into a tool that cannot run has two ways to go wrong and only
 * one of them is visible: it throws somewhere deeper, or it SUCCEEDS with
 * defaults nobody chose and hands back a file the user believes they
 * configured. Removing the controls is the only honest state — the banner says
 * the tool is not working and the page stops offering to work.
 *
 * The banner region survives, because it is where the banner is.
 */
function disableTool(root: HTMLElement): void {
  for (const sel of [
    '[data-dropzone-host]',
    '[data-option-panel]',
    '[data-parsed-preview]',
    // Its own entry since it stopped being a child of the preview: removing
    // the preview no longer takes it along.
    '[data-fps-field]',
    '[data-download]',
    '[data-result-diff]',
    '[data-result-table]',
    // A merge's file list and format choice, a split's second part.
    '[data-file-order]',
    '[data-write-format-field]',
    // A Word document's layout, shown from the start on /srt-to-word.
    '[data-docx-layout-field]',
    '[data-download-second]',
    '[data-parts-note]',
  ]) {
    qs<HTMLElement>(sel, root)?.remove();
  }
}

/*
 * ── THE READING-SPEED REPORT ──────────────────────────────────────────────
 *
 * A schema with a `qc.` field is a page whose result is a QC report: the
 * reading-speed checker. Its three controls used to reach nothing, because its
 * pipeline is `['normalize']`, no op has those fields, and this page never
 * asked the worker for a report. It normalised the file, showed the cues and
 * measured no speed whatever the user chose (see src/tools/qc-from-values.ts).
 *
 * The table and the summary slot are made HERE rather than in tools/tool.html
 * because that template was being edited on another branch when this landed;
 * both belong in the markup, beside the diff and the table it already
 * carries, and moving them is a template change with no logic in it.
 */

/** Give the page's cue table the metric column the CSS already styles. */
function asMetricsTable(host: HTMLElement): void {
  // `mountCueTable` reads this class once, at mount, to decide whether rows
  // get a metric cell, so it must be set before the table is mounted.
  host.classList.add('cue-table--metrics');
  const head = qs<HTMLElement>('.cue-table__head', host);
  if (head === null || qs('.cue-table__cell--metric', head) !== null) return;
  const cell = document.createElement('span');
  cell.className = 'cue-table__cell cue-table__cell--metric';
  cell.setAttribute('role', 'columnheader');
  // The page's own name for the number (its title is "… CPS and Line Length").
  cell.textContent = 'CPS';
  head.append(cell);
}

/** The findings list above the table. Caveats first: they qualify every number below. */
function renderQcSummary(host: HTMLElement, view: QcView): void {
  const list = (rows: readonly QcRow[], counted: boolean) => {
    const ul = document.createElement('ul');
    ul.className = 'stack';
    for (const row of rows) {
      const li = document.createElement('li');
      setData(li, 'severity', row.severity);
      li.textContent = counted ? `${row.label}: ${row.count}` : row.label;
      ul.append(li);
    }
    return ul;
  };
  const parts: Node[] = [];
  if (view.caveats.length > 0) parts.push(list(view.caveats, false));
  if (view.clean) {
    const p = document.createElement('p');
    p.textContent = qcSentence('nothing-flagged');
    parts.push(p);
  } else {
    parts.push(list(view.findings, true));
  }
  host.replaceChildren(...parts);
  host.hidden = false;
}

export function mountToolPage(): void {
  const root = qs<HTMLElement>('[data-tool-page]');
  const config = readConfig();
  if (!root || !config) return;
  // A link-only page (/youtube-summarizer, SEO_BRIEF_09 6.1) has the paste box and no drop zone:
  // nothing here runs on it, and pages/landing.ts drives the box (boot.ts).
  if (!qs('[data-dropzone-host]', root)) return;

  // ⚠ ABSENT `ops` IS A BROKEN COURIER, NOT A TOOL WITH NOTHING TO DO.
  //
  // `api.md` 1.7.0: every row declares `ops`. So the two states are different
  // facts and collapsing them is what let the previous defect ship —
  //
  //   absent            → nothing told this page what to run. REFUSE.
  //   present and empty → a conversion, which transforms nothing. RUN.
  //
  // `?? []` here instead would turn a delivery failure into a silent no-op that
  // returns the input file unchanged, which is exactly what `tool.op` was doing
  // on eight pages: a wrong file that looks right, the worst thing this product
  // can produce.
  //
  // The check is `!Array.isArray` rather than `=== undefined` because the
  // template emits `null` for a missing row key: `tojson` on a Jinja
  // `Undefined` raises, so omitting the default would 500 the page instead of
  // rendering the refusal. `null` and `[]` stay distinguishable, which is the
  // property that matters.
  if (!Array.isArray(config.ops)) {
    console.error('[tool] the page config carries no `ops` — refusing to run', config.tool.slug);
    disableTool(root);
    showState(must<HTMLElement>('#banner-region'), 'TOOL_UNAVAILABLE', {});
    return;
  }

  // AND THE OPS MUST BE BUILDABLE, checked HERE rather than at submit time.
  // Discovering it when the user clicks would mean a dropzone, a file read, a
  // parse, and then a failure: all the work, none of the result. So it is a
  // mount-time refusal for the same reason the schema check is. A tool that
  // cannot run must stop offering to.
  //
  // The check is made with STAND-IN files (`probeInputs`). Without them,
  // /merge-subtitles and /split-subtitles refused here while published: a
  // merge needs a second document's id, which exists only once a second file
  // is parsed, so "can this be built from options alone" was always no. The
  // question that matters is whether it can be built once the files are in.
  //
  // A page that needs more than one file in, or hands back more than one file,
  // also needs the markup that does so. Without it a merge would show no order
  // and a split would hand over part one and drop part two without a word.
  const shape = runShape(config.ops);
  try {
    opsFromValues(config.ops, {}, probeInputs(config.ops));
    if (shape.takesMany && !qs('[data-file-order]', root)) {
      throw new Error('a page that merges files has no file list to show their order in');
    }
    if (shape.givesParts && !qs('[data-download-second]', root)) {
      throw new Error('a page that hands back two parts has no button for the second');
    }
  } catch (err) {
    console.error('[tool] this tool cannot be driven from its options alone', err);
    disableTool(root);
    showState(must<HTMLElement>('#banner-region'), 'TOOL_UNAVAILABLE', {});
    return;
  }
  // Bound to fresh consts so the narrowing survives into the closures below.
  const page: ToolPageContext = config;
  const tool: ToolConfig = config.tool;
  // The usage counter (lib/usage.ts), under this page's own slug: the one the
  // server rendered into the config, which is the tools.json row's.
  const usage = usageCounter(tool.slug);
  // THE ONE FORMAT THIS PAGE WRITES. `write_formats` is a TOP-LEVEL context key
  // and a LIST. The runtime used to read `tool.write_format` — singular, and
  // nested inside `tool` — which the server has never emitted in any build.
  // First entry is the tool's own output: a converter tool writes one format,
  // and the row that lists several is a destination page, which picks per
  // download rather than per page.
  //
  // UNLESS THE OPS MAKE SOMETHING IT CANNOT HOLD. Pinyin 'above' is an
  // annotation, the pinyin row lists ASS first, and ASS has none: the page
  // wrote a file with no pinyin in it while VTT sat in the same list.
  // `writeFormatFor` (src/tools/write-format.ts) picks per run, from the
  // capability rows, and `setWriteFormat` keeps the button's label honest.
  let writeFormat: WriteFormat | undefined = config.write_formats?.[0];
  // The format of the file the user dropped, so a fixing tool can hand the same
  // one back (`writeFormatFor`). Unset for a batch, whose files can differ.
  let sourceFormat: string | undefined;
  // The rate that file was read at: the one it declares, or the one typed into
  // the FPS prompt. Null for every format that does not count in frames.
  let sourceRate: Framerate | null = null;

  const region = must<HTMLElement>('#banner-region');
  const dropHost = must<HTMLElement>('[data-dropzone-host]', root);
  const optionsHost = must<HTMLElement>('[data-option-panel]', root);
  const previewHost = qs<HTMLElement>('[data-parsed-preview]', root);
  const tableHost = qs<HTMLElement>('[data-cue-table]', root);
  const diffHost = qs<HTMLElement>('[data-diff]', root);
  const downloadButton = qs<HTMLButtonElement>('[data-download]', root);
  const encodingSelect = qs<HTMLSelectElement>('[data-encoding-override]', root);
  const encodingDetails = qs<HTMLDetailsElement>('[data-encoding-panel]', root);
  // /fix-subtitle-encoding: the panel is the tool, so it is open on every file
  // rather than only when the detector is unsure. The template says so.
  const encodingAlwaysOpen = encodingDetails?.hasAttribute('data-encoding-open') ?? false;
  const fpsField = qs<HTMLElement>('[data-fps-field]', root);
  const fpsInput = qs<HTMLInputElement>('[data-fps-input]', root);
  const batchList = qs<HTMLElement>('[data-batch-list]', root);
  const progressBar = qs<HTMLElement>('[data-tool-progress]', root);
  const outcomeHost = qs<HTMLElement>('[data-download-outcome]', root);
  const diffSection = qs<HTMLElement>('[data-result-diff]', root);
  const tableSection = qs<HTMLElement>('[data-result-table]', root);
  const refusalHost = qs<HTMLElement>('[data-options-refusal]', root);
  // The result on screen, selected and copied instead of downloaded (lib/copies.ts): counted
  // once per file's result, the table and the diff together.
  watchCopies([tableSection, diffSection], tool.slug);
  // The merge page's file list and format choice, and the split page's second
  // download. Absent on every other page; the mount check above has already
  // refused on a page whose ops need them and whose markup lacks them.
  const secondButton = qs<HTMLButtonElement>('[data-download-second]', root);
  const partsNote = qs<HTMLElement>('[data-parts-note]', root);
  const fileOrder = qs<HTMLElement>('[data-file-order]', root);
  const fileList = qs<HTMLElement>('[data-file-list]', root);
  const fileMore = qs<HTMLElement>('[data-file-more]', root);
  const fileReset = qs<HTMLButtonElement>('[data-file-reset]', root);
  const formatField = qs<HTMLElement>('[data-write-format-field]', root);
  const formatSelect = qs<HTMLSelectElement>('[data-write-format-select]', root);
  // The text encoding (lib/save-encoding.ts), shown while the format being
  // written is SMI, or SRT when the subtitles are Korean (`subtitleText`
  // below tells it which). A change writes the file again, the same way a
  // format change does, so the count of characters EUC-KR can't hold is on
  // screen before the download.
  const saveEncoding = mountSaveEncoding(root, () => void run());
  saveEncoding.sync(writeFormat);
  // A Word document's layout (lib/docx.ts), shown while the format being
  // written is DOCX. A change writes the file again, as an encoding change does.
  const docxLayout = mountDocxLayout(root, () => void run());
  docxLayout.sync(writeFormat);
  // A PDF's layout (lib/pdf-layout.ts, SEO_BRIEF_08 section 7.1): with times or text only, shown
  // while the format being written is PDF. A change writes the file again.
  const pdfLayout = mountPdfLayout(root, () => void run());
  pdfLayout.sync(writeFormat);
  // EBU STL's frame rate and TTML's extension (lib/tv-formats.ts), shown while
  // the format being written is EBU STL or TTML. A rate change writes the file
  // again; an extension change only renames the download.
  const stlRate = mountStlRate(root, () => void run());
  stlRate.sync(writeFormat);
  const ttmlExtension = mountTtmlExtension(root);
  ttmlExtension.sync(writeFormat);
  // A frame-counting download (MicroDVD back to MicroDVD) is counted in the
  // rate the file was read at; `writeFramerate` says when and why. EBU STL is
  // counted in the rate picked on the page.
  const writeOptionsFor = (format: WriteFormat) => {
    const framerate =
      stlRate.framerateFor(format) ?? writeFramerate(format, sourceRate, config.ops ?? []);
    return downloadWriteOptions(format, {
      encoding: saveEncoding.encodingFor(format),
      formatOptions: docxLayout.formatOptionsFor(format) ?? pdfLayout.formatOptionsFor(format),
      ...(framerate === null ? {} : { framerate }),
    });
  };

  const client = worker({
    onProgress: (_reqId, done, total) => paintProgress(done, total),
    onRespawn: () => {
      // Everything the worker held is gone. Saying so beats a page that looks
      // loaded and answers nothing.
      source.forget();
      pinyinSent = false;
      output = null;
      secondOutput = null;
      files = [];
      paintFiles();
      showInTable(null, 0);
      showState(region, 'NETWORK', {});
    },
  });

  // The dropped file and the result shown are separate documents in the worker:
  // every option change re-runs on a fresh copy of the file, never on the last
  // result. See lib/tool-source.ts for what stacking cost on the live site.
  const source = createToolSource(client);
  /**
   * The file being worked on, kept so the MicroDVD FPS prompt can parse it
   * again with the rate the user typed: the bytes sent to `parse` transfer,
   * and a parse that failed retains nothing in the worker.
   */
  let currentFile: File | null = null;
  /** Bumped by every ingest, so a slow parse cannot land over a newer drop. */
  let ingestGeneration = 0;
  /**
   * The pinyin table goes over once per worker rather than once per option
   * change: it is 20,924 entries and every `postMessage` clones all of them.
   */
  let pinyinSent = false;
  let sourceName = 'subtitles';
  let diffRows: DiffRow[] = [];
  let diffSummary = '';
  /** `archive`: a batch's ZIP of converted files, rather than one file. */
  let output: { bytes: Uint8Array | string; filename: string; archive?: boolean } | null = null;
  /** A split's part two, written beside part one and offered by its own button. */
  let secondOutput: { bytes: Uint8Array | string; filename: string } | null = null;
  /**
   * The format picked on a page with a save-as choice (a merge, a colour, a position). It
   * outranks the first file's, or the row's first, once chosen.
   */
  let chosenFormat: WriteFormat | undefined;
  /** A merge's files as its list shows them, in the order they combine. */
  let files: { readonly name: string; readonly format: string; readonly cueCount: number }[] = [];
  /** Drops queue behind each other on a merge, so files join in the order they came. */
  let adding: Promise<void> = Promise.resolve();
  let values: OptionValues = {};

  /* The engine refusing these settings: see lib/refusal.ts. */
  /** The latest finished run was refused, so it produced nothing to download. */
  let refused = false;
  /** A run is in flight; what is on screen answers the values before it. */
  let runPending = false;
  /** Bumped by every run, so only the newest one clears `runPending`. */
  let runGeneration = 0;
  /** The user is mid-edit in an option box. */
  let typing = false;
  /** Option keys the user, or the page from the file, has put a value in. */
  const entered = new Set<string>();
  const awaited = awaitedFields(tool.options_schema);

  /**
   * The document on screen: the last finished run's result, or on a page that picks its
   * points off the table, the file as dropped (`showSource`). The cue table pages through it.
   */
  let tableDoc: DocumentId | null = null;
  const cueWindow = createCueWindow({
    fetchPage: (from, count) => {
      if (!tableDoc) return Promise.reject(new Error('no document'));
      return client.request('preview', { docId: tableDoc, from, count });
    },
    onPage: () => table?.refresh(),
  });

  /** Point the cue table at `id` and repaint it. */
  function showInTable(id: DocumentId | null, count: number): void {
    tableDoc = id;
    cueWindow.reset(id, count);
    table?.refresh();
  }

  /*
   * PICKING A POINT OFF THE TABLE, on /sync-subtitles only (src/tools/point-pick.ts). A page
   * whose schema has the two-point sync's boxes gets rows that fill a "Time in the file"
   * when clicked; every other page's table stays read-only, because `pointPairs` is empty
   * there and no `onPick` is passed.
   */
  const points = pointPairs([...walkFields(tool.options_schema)].map((f) => f.key));
  const picking = points.length > 0;
  /** The option box the user was last in, by key, for `pickTarget`. */
  let lastFocusedKey: string | null = null;

  /** True on a page whose result is the reading-speed report. */
  const runsQc = hasQcControls([...walkFields(tool.options_schema)].map((f) => f.key));
  /** The last report's speed per cue id, for the metric column. */
  let speeds: ReadonlyMap<string, CueSpeed> = new Map();
  const qcHost = runsQc ? document.createElement('div') : null;
  if (qcHost) {
    qcHost.className = 'stack';
    qcHost.hidden = true;
    qcHost.setAttribute('aria-live', 'polite');
    setData(qcHost, 'qc-summary', 'true');
    (diffSection ?? tableSection)?.before(qcHost);
  }
  if (runsQc && tableHost) asMetricsTable(tableHost);
  const withSpeed = (row: CueRow | null): CueRow | null => {
    const speed = row?.id === undefined ? undefined : speeds.get(row.id);
    return row && speed ? { ...row, metric: formatCps(speed.cps), over: speed.over } : row;
  };
  const rows: CueTableSource = runsQc
    ? { count: () => cueWindow.count(), row: (i) => withSpeed(cueWindow.row(i)) }
    : cueWindow;
  const table = tableHost
    ? mountCueTable(tableHost, rows, picking ? { onPick: pickRow } : {})
    : null;
  const pickHint = qs<HTMLElement>('[data-pick-hint]', root);
  if (picking && pickHint) {
    pickHint.hidden = false;
    if (tableHost && pickHint.id) tableHost.setAttribute('aria-describedby', pickHint.id);
  }
  const diff = diffHost
    ? mountDiffView(diffHost, {
        count: () => diffRows.length,
        row: (i) => diffRows[i] ?? null,
        summary: () => diffSummary,
      })
    : null;

  function paintProgress(done: number, total: number): void {
    if (!progressBar) return;
    progressBar.hidden = total <= 0;
    if (total > 0) {
      const pct = Math.max(0, Math.min(100, (done / total) * 100));
      progressBar.style.setProperty('--progress-pct', `${pct.toFixed(1)}%`);
    }
  }

  /* ── options ────────────────────────────────────────────────────────── */

  let panel: ReturnType<typeof mountOptionPanel> | null = null;
  try {
    panel = mountOptionPanel(optionsHost, tool.options_schema, (next) => {
      // Picking a reading-speed standard sets the two boxes to its numbers;
      // otherwise "Netflix — Chinese (11 characters/second)" flagged at the
      // latin 20 still sitting in the box.
      const follow = runsQc ? qcProfileFollowers(values, next) : null;
      values = follow ? { ...next, ...follow } : next;
      if (follow) panel?.setValues(follow);
      void run();
    });
  } catch (err) {
    // A schema this renderer cannot draw is a conversation with the feature
    // that published it. Failing loudly beats drawing an approximation of
    // someone else's tool.
    //
    // ⚠ THIS USED TO CALL showState('NETWORK') AND IT SHIPPED THAT WAY. All
    // fourteen tool pages told the user "We couldn't reach the server. Nothing
    // was started and your free minutes weren't used" — on a page that contacts
    // no server and has no minutes, under a badge reading "Runs entirely in
    // your browser". The comment above diagnosed it correctly and the next line
    // routed it to the state that blames the user's connection.
    //
    // An undrawable schema decides whether the TOOL EXISTS, so it does not get
    // the vocabulary of a transient failure.
    console.error('[tool] option panel refused the schema', err);
    showState(region, 'TOOL_UNAVAILABLE', {});
    disableTool(root);
    return;
  }

  /*
   * /auto-sync-subtitles (lib/autosync.ts): a video or audio file beside the subtitle file, and
   * the match that fills this page's boxes in. Null on every page without its markup.
   */
  const autosync = mountAutosync(root, region, {
    hasSubtitles: () => source.inputId !== null,
    refuseVideo: (file) => refuseVideo(file),
    async timings() {
      // The file as dropped, never a result: a second match must not start from the first.
      const reference = await source.reference();
      if (reference === null) return null;
      const rows: CuePreviewRow[] = [];
      for (let from = 0; from < reference.cueCount; from += 1000) {
        const page = await client.request('preview', { docId: reference.docId, from, count: 1000 });
        rows.push(...page.rows);
      }
      return cueTimings(rows);
    },
    setValues(next, rerun) {
      values = { ...values, ...next };
      for (const key of Object.keys(next)) entered.add(key);
      panel?.setValues(next);
      if (rerun) void run();
    },
  });

  /**
   * B5_VIDEO_FORMAT: a video no reader on this page opens (lib/dropzone.ts), counted as `video`
   * so the stats tell it apart from a random file. A page that takes a recording, a transcription
   * page or /auto-sync-subtitles, adds that the audio file works too.
   */
  function refuseVideo(file: File): void {
    usage.failed(file, 'video');
    const takesRecording = page.input === 'media' || autosync !== null;
    showState(region, 'B5_VIDEO_FORMAT', {
      format: videoTypeName(file.name),
      ...(takesRecording ? { audio: 'y' } : {}),
    });
  }

  /**
   * Take the tool off the page, rather than leaving a dropzone above an error.
   *
   * A file dropped into a tool with no options runs an op with no parameters,
   * which either throws deeper in or — worse — succeeds with defaults nobody
   * chose and hands back a file the user believes they configured. Removing the
   * controls is the only honest state: the banner says the tool is not working,
   * and the page stops offering to work.
   *
   * The banner region survives, because it is where the banner is.
   */
  /* ── the worker pipeline ────────────────────────────────────────────── */

  /**
   * Hand the encoding field the text of the subtitles being saved, so it can
   * tell Korean ones (lib/save-encoding.ts). One window of rows from the
   * worker, never the document, and only for a format whose field depends on
   * it: an SRT write asks, a VTT or SMI write does not.
   */
  async function subtitleText(docId: DocumentId, format: WriteFormat): Promise<void> {
    if (!saveEncoding.needsText(format)) return;
    const { rows } = await client.request('preview', { docId, from: 0, count: TEXT_SAMPLE_CUES });
    saveEncoding.setText(rows.map((row) => row.text));
  }

  /**
   * A PDF of Polish, Russian or Greek subtitles needs a font embedded in it (lib/pdf-fonts.ts),
   * fetched once and resident in the worker before the availability check asks the writer. A
   * fetch that fails leaves the check to refuse the PDF as it did before the font existed.
   */
  async function pdfFonts(docId: DocumentId, format: WriteFormat, cues: number): Promise<void> {
    if (format !== 'pdf') return;
    const { rows } = await client.request('preview', { docId, from: 0, count: cues });
    try {
      await ensurePdfFonts(
        client,
        rows.map((row) => row.text),
      );
    } catch (err) {
      if (!(err instanceof PdfFontLoadError)) throw err;
      console.warn('[tool]', err.message);
    }
  }

  /** Write `next` from now on, and say so on the button. */
  function setWriteFormat(next: WriteFormat | undefined): void {
    if (next === undefined || next === writeFormat) return;
    for (const button of [downloadButton, secondButton]) {
      if (!button || !writeFormat) continue;
      // The template's own words, with only the format token swapped.
      const label = button.textContent ?? '';
      button.textContent = label.replace(writeFormat.toUpperCase(), next.toUpperCase());
    }
    writeFormat = next;
    if (formatSelect) formatSelect.value = next;
    saveEncoding.sync(next);
    docxLayout.sync(next);
    pdfLayout.sync(next);
    stlRate.sync(next);
    ttmlExtension.sync(next);
  }

  async function run(): Promise<void> {
    if (source.inputId === null) return;
    let ticket: ToolRun | null = null;
    const generation = ++runGeneration;
    runPending = true;
    try {
      // The file the options describe is about to change, so the bytes from the
      // last run must not be downloadable in the meantime: they answer options
      // the user has since edited.
      output = null;
      secondOutput = null;
      if (downloadButton) downloadButton.disabled = true;
      if (secondButton) secondButton.disabled = true;

      // A MERGE OF ONE FILE IS NOT A MERGE. Run anyway and it would hand back
      // the first file renumbered under a working Download button. The file
      // stays on screen as parsed, the list asks for another, and nothing is
      // offered until one arrives.
      if (shape.takesMany && source.others().length === 0) {
        outcomeHost?.replaceChildren();
        return;
      }

      // The ops are built once the run's fresh copy exists, because a split
      // "before subtitle number" reads that subtitle's time off the copy.
      ticket = await source.run(async (copy) => {
        const ops = opsToApply(page, values, await inputsFor(copy));
        setWriteFormat(chosenFormat ?? writeFormatFor(ops, page.write_formats ?? [], sourceFormat));
        // LAZY, AND BEFORE THE OP. 402 KB of Hanzi readings, fetched only on the
        // one page that runs a PinyinOp and only the first time it runs one — the
        // whole reason `adapter.md` §9 says "never statically bundled" is that an
        // import would ship it to everyone converting an SRT.
        //
        // Ordering makes the handshake unnecessary: `pinyin` is fire-and-forget
        // and MessagePort delivery is ordered, so a table posted here arrives
        // before the `apply` posted after it. Awaiting the FETCH is what matters,
        // not awaiting the worker.
        if (!pinyinSent && ops.some((o) => String(o['op']).toLowerCase().includes('pinyin'))) {
          client.sendPinyinTable(await loadPinyinTable());
          pinyinSent = true;
        }
        // Ops are plain JSON by contract — no live RegExp, no Map, no closures.
        return ops;
      });
      if (ticket === null) return;
      // This run's format, held still: a newer run may change the page's. Read
      // here, after the build set it, and safe to: a run that a newer one has
      // started over gets `null` above rather than a ticket.
      const format = writeFormat;
      const { applied } = ticket;

      // The op changed the cues, so every cached page is stale. The window
      // refetches what is on screen and nothing else. Not on a page that picks
      // its points off the table: that table shows the file as dropped, which
      // no run changes.
      if (!picking) showInTable(applied.docId, applied.cueCount);

      if (diff) await paintDiff(applied.docId, ticket);
      if (!ticket.current()) return;
      if (runsQc) await paintQc(applied.docId, ticket);
      if (!ticket.current()) return;
      // A checker's result is its report, read on the page: leaving it is not a no-result.
      if (runsQc) resultReached(tool.slug);

      // REFUSED: the engine would not run an op as configured and handed the
      // document back unchanged. Writing it would put the file as dropped
      // behind a Download button that says the tool ran, which is how
      // /sync-subtitles at 6772448 "synced" with two equal or reversed points.
      // No write, so there are no bytes to hand over; the table above still
      // shows the file, and the message under the options says why.
      refused = isRefusal(ticket.diagnostics);
      if (refused) {
        output = null;
        outcomeHost?.replaceChildren();
        if (downloadButton) {
          downloadButton.hidden = false;
          downloadButton.disabled = true;
        }
        if (secondButton) secondButton.hidden = false;
        // A split with no cut point yet is refused, and that is the waiting
        // state rather than a mistake: say what to do.
        setPartsNote('waiting');
        return;
      }

      if (format && downloadButton) {
        // What the subtitles say, before anything reads the encoding: SRT is
        // offered EUC-KR only when they are Korean, and a pick made on the
        // last file must not follow this one if it is not.
        await subtitleText(applied.docId, format);
        if (!ticket.current()) return;
        await pdfFonts(applied.docId, format, applied.cueCount);
        if (!ticket.current()) return;
        // ASK BEFORE WRITING. This is the half A6 could not do: `availability`
        // reports what each target would cost from the document itself, so a
        // refusal is known BEFORE the bytes exist rather than read off the
        // result. On a tool page the format is the user's choice, and being
        // told after the write that the choice was wrong is the failure.
        const choice = await availabilityOf(applied.docId, format);
        if (!ticket.current()) return;
        if (choice && outcomeHost) {
          const encoding = saveEncoding.encodingFor(format);
          renderOutcome(
            outcomeHost,
            choice,
            labelWithEncoding(format.toUpperCase(), encoding),
            encoding,
          );
        }
        if (choice && !mayDownload(choice)) {
          // No write at all — not a write whose result is discarded. Producing
          // bytes nobody may have is work done to be thrown away, and it is one
          // refactor away from those bytes reaching a button.
          output = null;
          downloadButton.hidden = true;
          if (secondButton) secondButton.hidden = true;
          setPartsNote(null);
          return;
        }
        downloadButton.hidden = false;
        if (secondButton) secondButton.hidden = false;

        // An empty part one (a cut before the first subtitle) has nothing to
        // hand over: written, it is a zero-byte file behind a working button.
        if (!shape.givesParts || applied.cueCount > 0) {
          const written = await client.request('write', {
            docId: applied.docId,
            format,
            options: writeOptionsFor(format),
          });
          if (!ticket.current()) return;
          // CORE names the file: `outputName`/`archiveName` in
          // core/adapter/filenames.ts already apply the no-prefix, no-suffix,
          // no-watermark rule, and a second implementation here would be a
          // second place for it to drift. A split's two parts are the one
          // exception, and `partName` says why.
          output = {
            bytes: written.output,
            filename: shape.givesParts ? partName(written.filename, 1) : written.filename,
          };
          downloadButton.disabled = false;
        }
        if (shape.givesParts) await writeSecondPart(ticket, format);
      }
    } catch (err) {
      // A run a newer one overtook can fail on a document that newer run has
      // already freed. Nobody is looking at it, so it gets no banner.
      if (ticket !== null && !ticket.current()) return;
      // A run that failed outright gets its banner; it is not a refusal.
      refused = false;
      handleWorkerError(err);
    } finally {
      if (progressBar) progressBar.hidden = true;
      if (generation === runGeneration) {
        runPending = false;
        paintRefusal();
      }
    }
  }

  /**
   * What the files supply to one run (`RunInputs`): a merge's other documents,
   * in the order they were added, and the start of the subtitle a split is cut
   * before.
   *
   * That start is read off `copy`, the run's fresh copy of the file as
   * dropped, and not off what is on screen: after a split, what is on screen is
   * part one, renumbered, and its subtitle 40 is not the file's.
   */
  async function inputsFor(copy: DocumentId): Promise<RunInputs> {
    const others = source.others();
    const cue = cueNeeded(page.ops ?? [], values as Readonly<Record<string, unknown>>);
    if (cue === null) return { others };
    const found = await client.request('preview', {
      docId: copy,
      from: Math.max(0, Math.floor(cue) - 1),
      count: 1,
    });
    const row = found.rows[0];
    // No row: the file has fewer subtitles than the number typed. `null` says
    // so, and the op then cuts after the last one (part two comes back empty).
    return { others, cueStart: row === undefined ? null : (row.startTicks as Ticks) };
  }

  /**
   * Part two of a split: its own write, its own button, and the line under
   * both. It is the spawned document of the same run, so it has the same
   * format and passed the same availability check as part one.
   */
  async function writeSecondPart(ticket: ToolRun, format: WriteFormat): Promise<void> {
    const second = ticket.applied.spawned[0];
    if (second === undefined) {
      // A split that ran and spawned nothing is a defect, not an empty part.
      throw new Error('the split ran and handed back no second part');
    }
    const { total } = await client.request('preview', { docId: second, from: 0, count: 0 });
    if (!ticket.current()) return;
    if (total > 0) {
      const written = await client.request('write', {
        docId: second,
        format,
        options: writeOptionsFor(format),
      });
      if (!ticket.current()) return;
      secondOutput = { bytes: written.output, filename: partName(written.filename, 2) };
      if (secondButton) secondButton.disabled = false;
    }
    const first = ticket.applied.cueCount;
    setPartsNote(first === 0 || total === 0 ? 'empty' : 'counts', {
      first: String(first),
      second: String(total),
    });
  }

  /** The line under a split's two buttons, from the template's own sentences. */
  function setPartsNote(
    which: 'waiting' | 'counts' | 'empty' | null,
    slots: Readonly<Record<string, string>> = {},
  ): void {
    if (!partsNote) return;
    // Document-scoped, as batchLabel is: `root`'s narrowing does not survive
    // into this closure, and the labels are unique on the page.
    const label = which === null ? null : qs<HTMLElement>(`[data-parts-label="${which}"]`);
    let text = '';
    if (label) {
      const copy = label.cloneNode(true) as HTMLElement;
      for (const [slot, value] of Object.entries(slots)) {
        const node = qs<HTMLElement>(`[data-slot="${slot}"]`, copy);
        if (node) node.textContent = value;
      }
      text = copy.textContent?.replace(/\s+/g, ' ').trim() ?? '';
    }
    // Only on a real change: rewriting a live region with the same words
    // announces them again.
    if (partsNote.textContent !== text) partsNote.textContent = text;
  }

  /* ── a merge's files ─────────────────────────────────────────────────── */

  /** The numbered list of a merge's files, first one marked. */
  function paintFiles(): void {
    if (!fileOrder || !fileList) return;
    fileOrder.hidden = files.length === 0;
    if (fileMore) fileMore.hidden = files.length >= 2;
    if (formatField) formatField.hidden = files.length === 0;
    const row = qs<HTMLTemplateElement>('template[data-file-row]', fileOrder);
    if (!row) return;
    fileList.replaceChildren(
      ...files.map((file, i) => {
        const item = row.content.cloneNode(true) as DocumentFragment;
        const set = (slot: string, text: string) => {
          const node = qs<HTMLElement>(`[data-slot="${slot}"]`, item);
          if (node) node.textContent = text;
        };
        set('n', String(i + 1));
        set('name', shortFilename(file.name));
        set('format', file.format.toUpperCase());
        set('cues', String(file.cueCount));
        // Both word forms are in the row; the count keeps one.
        qs(`[data-plural="${file.cueCount === 1 ? 'other' : 'one'}"]`, item)?.remove();
        if (i > 0) qs('[data-file-first]', item)?.remove();
        return item;
      }),
    );
  }

  /**
   * A MERGE PAGE'S DROP ADDS TO THE LIST rather than replacing what is there:
   * the common way to give a page two files is to choose one and then the
   * other. Drops queue behind each other and each file is parsed in turn, so
   * the order on screen is the order they came in, whatever finishes first.
   */
  function addFiles(list: readonly File[], framerate: Framerate | null = null): void {
    const mine = ingestGeneration;
    adding = adding.then(async () => {
      let added = false;
      for (const file of list) {
        if (mine !== ingestGeneration) return;
        added = (await addFile(file, framerate)) || added;
      }
      if (added && mine === ingestGeneration) await run();
    });
  }

  /** Parse one file onto the end of the list. True when it joined. */
  async function addFile(file: File, framerate: Framerate | null): Promise<boolean> {
    sourceName = file.name;
    const kind = classify(file);
    if (kind === 'audio') {
      // B6, as on every tool page: an audio file is transcribed, not merged.
      void uploads.offer(file);
      return false;
    }
    if (kind === 'other-video') {
      refuseVideo(file);
      return false;
    }
    if (kind === 'unknown') {
      usage.failed(file, failedFormat(extensionOf(file.name), null));
      showState(region, 'B5_UNSUPPORTED_BINARY', unreadableKind(file.name));
      return false;
    }
    if (kind === 'archive') {
      // Not a batch here: a merge takes the files themselves. Parsed, the
      // worker refused it as an archive and the page showed the bitmap banner.
      usage.failed(file, 'zip');
      showState(region, 'B5_ARCHIVE_HERE', {});
      return false;
    }
    const mine = ingestGeneration;
    const first = files.length === 0;
    currentFile = file;
    if (file.size > LARGE_FILE_BYTES && kind !== 'container') {
      const frag = renderState('B7', {
        filename: shortFilename(file.name),
        size: fileSize(file.size),
      });
      if (frag) region.replaceChildren(frag);
    }
    try {
      let info: ParsedInfo;
      let byteLength: number;
      if (kind === 'container') {
        // A video's text track joins the list like a file (lib/video-flow.ts).
        const picked = await videoTrackOnce(file, first, mine);
        if (picked === null) return false;
        info = picked;
        byteLength = picked.byteLength;
      } else {
        const bytes = new Uint8Array(await file.arrayBuffer());
        byteLength = bytes.byteLength;
        // A ZIP is parsed like the rest and refused by the worker ('is-archive'):
        // the batch path writes one file per entry, which is not a merge.
        info = await client.request('parse', {
          inputName: file.name,
          bytes,
          retainRaw: false,
          // The first file is the source every run copies, so its bytes stay,
          // as on any tool page. The rest are only ever read.
          retainBytes: first,
          ...(framerate === null ? {} : { framerate }),
        });
      }
      if (mine !== ingestGeneration) {
        client.dispose([info.docId], [info.inputId]);
        return false;
      }
      if (first) {
        source.load(info);
        sourceFormat = info.format;
        sourceRate = info.framerate;
        showInTable(info.docId, info.cueCount);
        paintParsed(info, file, byteLength);
      } else {
        source.addOther(info);
      }
      files = [...files, { name: file.name, format: info.format, cueCount: info.cueCount }];
      if (first && !chosenFormat) {
        // The list says the merged file takes the first file's format, so the
        // picker starts there.
        setWriteFormat(writeFormatFor([], page.write_formats ?? [], info.format));
      }
      paintFiles();
      // Once per file: a merge of three files counts three.
      usage.loaded(file, info.format);
      track('tool_used', { tool: tool.slug });
      return true;
    } catch (err) {
      // A file that fails to parse is left out and says why; the files
      // already in the list stay.
      if (mine === ingestGeneration) {
        if (!asksForRate(err)) usage.failed(file, failedFormat(extensionOf(file.name), err));
        handleWorkerError(err);
      }
      return false;
    } finally {
      drop.setLoaderStatus(null);
    }
  }

  /** "Start again with other files": the list, the result and every document go. */
  function resetFiles(): void {
    ingestGeneration += 1;
    currentFile = null;
    chosenFormat = undefined;
    sourceFormat = undefined;
    sourceRate = null;
    files = [];
    clearResult();
    paintFiles();
    region.replaceChildren();
  }

  /**
   * The sentence under the options, from the labels the template carries: the
   * first op this page runs that has its own, else the general one.
   */
  function refusalText(): string {
    // Document-scoped, as batchLabel is: `root`'s narrowing does not survive
    // into this closure, and the labels are unique on the page.
    for (const op of page.ops ?? []) {
      const own = qs<HTMLElement>(`[data-refusal-label="${CSS.escape(op)}"]`);
      if (own?.textContent) return own.textContent.trim();
    }
    return qs<HTMLElement>('[data-refusal-label="*"]')?.textContent?.trim() ?? '';
  }

  function paintRefusal(): void {
    if (!refusalHost) return;
    // While a run is in flight, what is on screen answers the values before
    // it; leave it until the run lands. Typing always clears it.
    if (runPending && !typing) return;
    const text = refusalShown({ refused, typing, awaited, entered, values }) ? refusalText() : '';
    // Only on a real change: rewriting a live region with the same words
    // announces them again.
    if (refusalHost.textContent !== text) refusalHost.textContent = text;
  }

  /**
   * What this format would cost for THIS document, before writing it.
   *
   * Returns null rather than throwing when the worker refuses: an availability
   * answer is an improvement on the download step, not a precondition for it.
   * A build whose worker has no `availability` handler should still hand over a
   * file, and losing the warning is a smaller failure than losing the download.
   */
  async function availabilityOf(
    id: DocumentId,
    format: WriteFormat,
  ): Promise<DownloadChoice | undefined> {
    try {
      const { format: _drop, ...base } = writeOptionsFor(format);
      const payload = await client.request('availability', {
        docId: id,
        // Non-empty by contract — the worker refuses an absent or empty list
        // rather than reading it as "all", because a row told "nothing is
        // available" is indistinguishable from a correct answer.
        targets: [format],
        options: base,
      });
      return downloadChoices(payload.formats)[0];
    } catch (err) {
      console.error('[tool] availability unavailable, offering the download anyway', err);
      return undefined;
    }
  }

  async function paintDiff(id: DocumentId, ticket: ToolRun): Promise<void> {
    if (!diff) return;
    // Against the copy's own baseline, which is the untouched parse: the diff
    // shows what THESE options do to the file, not what the last edit did.
    const report = await client.request('diff', { docId: id, baselineRev: 0 });
    if (!ticket.current()) return;
    // The legend comes from the COUNTS and the rows come from the ARRAYS, and
    // they are two calls because they answer differently under `countsOnly` and
    // under a `window`. Building the legend from `rows.length` is the bug this
    // separation exists to prevent: the arrays narrow, the counts never do.
    const summary = diffSummaryOf(report, { split: shape.givesParts });
    diffRows = [...rowsOfDiff(report, { split: shape.givesParts })];
    diffSummary = summary.text;
    diff.refresh();

    // WHICH RESULT VIEW, decided from the diff rather than from a config field
    // the server cannot send. An op that changed nothing has nothing to show in
    // a before/after, and a diff reading "no changes" beside a cue table full
    // of cues is the page arguing with itself.
    if (diffSection) diffSection.hidden = summary.empty;
    // Except where the points are picked off the table: there it shows the file
    // as dropped, not the result, and it is needed most once the first point
    // has changed the result, to pick the second.
    if (tableSection) tableSection.hidden = !picking && !summary.empty;
  }

  /** The file as dropped, in the cue table, so a row's time is the time in the file. */
  async function showSource(): Promise<void> {
    const reference = await source.reference();
    // Null: a newer file or encoding took over while it was made, and shows itself.
    if (reference !== null) showInTable(reference.docId, reference.cueCount);
  }

  /**
   * A row picked off the table on /sync-subtitles: its start goes into a "Time in the file"
   * box (which one is `pickTarget`'s rule), with the events typing it would fire, so the tool
   * runs again and the box counts as filled. Then on to that point's "Time it should have",
   * which is the one thing left to type.
   */
  function pickRow(row: CueRow): void {
    if (row.start === undefined) return;
    const pair = pickTarget(points, {
      filled: (key) => entered.has(key) && values[key] !== null && values[key] !== undefined,
      lastFocused: lastFocusedKey,
    });
    if (pair === null) return;
    const box = (key: string) =>
      qs<HTMLInputElement>(`[data-field="${CSS.escape(key)}"] input`, optionsHost);
    const at = box(pair.at);
    if (!at) return;
    // A group the user folded away would swallow the focus below.
    const group = at.closest('details');
    if (group && !group.open) group.open = true;
    at.value = row.start;
    at.dispatchEvent(new Event('input', { bubbles: true }));
    at.dispatchEvent(new Event('change', { bubbles: true }));
    const to = box(pair.to);
    if (to) {
      to.focus();
      // Typing replaces what is there, the 0 it starts with included.
      to.select();
    }
  }

  /**
   * The reading-speed report for the document just produced, under the
   * settings on screen. Its numbers go into the table's metric column, so on
   * this page the table is the result even when `normalize` changed something:
   * a before/after of a re-sort is not what anyone came to a checker for.
   */
  async function paintQc(id: DocumentId, ticket: ToolRun): Promise<void> {
    const settings = qcSettingsFromValues(values);
    const report = await client.request('qc', { docId: id, settings });
    if (!ticket.current()) return;
    speeds = cueSpeeds(report, settings);
    if (diffSection) diffSection.hidden = true;
    if (tableSection) tableSection.hidden = false;
    table?.refresh();
    if (qcHost) renderQcSummary(qcHost, qcView(report));
  }

  function handleWorkerError(err: unknown): void {
    // The version handshake refusing to run is not a parse error and must not
    // be dressed as one. CORE's client fails loudly here by design.
    if (isVersionMismatch(err)) {
      console.error('[tool]', err);
      showState(region, 'STALE_PAGE', {});
      return;
    }
    if (err instanceof WorkerFailure) {
      const reason = err.reason;
      // `needs-framerate` is not an error, it is the MicroDVD FPS prompt —
      // which is a feature, and the one the competitor gets wrong by
      // defaulting to 23.976.
      if (asksForRate(err)) {
        if (fpsField) fpsField.hidden = false;
        fpsInput?.focus();
        return;
      }
      if (reason === 'is-archive') {
        // A ZIP has to go through `batch` so every entry gets a per-file
        // status. The dropzone routes it there; reaching here means a .zip
        // arrived without its extension.
        showState(region, 'B5_UNSUPPORTED_BINARY', {});
        return;
      }
      if (reason === 'unsupported-binary' || reason === 'unknown-format') {
        showState(region, 'B5_UNSUPPORTED_BINARY', unreadableKind(sourceName, err.detail));
        return;
      }
      if (reason === 'empty' || reason === 'no-cues') {
        showState(region, 'B5_EMPTY', {});
        return;
      }
      const detail = err.detail as { line?: number; message?: string } | null;
      showState(region, 'B5_PARSE_FAILED', {
        format: (writeFormat ?? '').toUpperCase(),
        line: detail?.line === undefined ? '' : String(detail.line),
        detail: detail?.message ?? reason,
      });
      return;
    }
    console.error('[tool]', err);
    showState(region, 'NETWORK', {});
  }

  /* ── parsed preview (B2) ────────────────────────────────────────────── */

  /** `shown` names the file as dropped when that is not what was parsed: a Word document or a PDF. */
  function paintParsed(
    info: ParsedInfo,
    file: File,
    byteLength: number,
    shown: DocumentKind | null = null,
  ): void {
    if (!previewHost) return;
    previewHost.hidden = false;
    const set = (name: string, text: string) => {
      const node = qs<HTMLElement>(`[data-slot="${name}"]`, previewHost);
      if (node) node.textContent = text;
    };
    set('filename', shortFilename(file.name));
    set('format', (shown ?? info.format).toUpperCase());
    set('cue_count', String(info.cueCount));
    set('encoding', info.encoding.label);
    set('size', fileSize(byteLength));

    if (encodingSelect) encodingSelect.value = info.encoding.label;
    // `needsEncodingOverride` is the worker's own below-0.7 test, so the rule
    // lives in one place. CORE's zero-dependency detector reports low
    // confidence on every legacy fixture it nonetheless decodes correctly, so
    // OPEN is likely the common case — which is why the opened panel is styled
    // as a labelled control rather than a warning.
    if (encodingDetails) encodingDetails.open = encodingAlwaysOpen || info.needsEncodingOverride;

    // Shown whenever the format is frame-based. Never guessed: a null
    // framerate on a frame-based read is the FPS prompt, not a default.
    if (fpsField) fpsField.hidden = info.framerate === null && info.framerateSource === null;
    if (fpsInput && info.framerate) {
      fpsInput.value = `${info.framerate.num}/${info.framerate.den}`;
      fpsInput.setAttribute('aria-invalid', 'false');
    }
    if (info.framerate) prefillSourceRate(info.framerate);
  }

  /**
   * Take the last file's result off the page: its rows, its diff, its parsed
   * preview, its loss banner and, above all, its bytes behind Download.
   */
  function clearResult(): void {
    source.clear();
    output = null;
    secondOutput = null;
    // Nothing says what the next file is until a run reads it: an SRT pick
    // of EUC-KR made on a Korean file hides, and UTF-8 applies, until then.
    saveEncoding.setText(null);
    if (downloadButton) downloadButton.disabled = true;
    if (secondButton) secondButton.disabled = true;
    setPartsNote(null);
    showInTable(null, 0);
    diffRows = [];
    diffSummary = '';
    diff?.refresh();
    speeds = new Map();
    if (qcHost) {
      qcHost.hidden = true;
      qcHost.replaceChildren();
    }
    if (previewHost) previewHost.hidden = true;
    outcomeHost?.replaceChildren();
    if (batchList) batchList.hidden = true;
    refused = false;
    paintRefusal();
  }

  /* ── the file's own framerate, into the fps converter ──────────────── */

  const SOURCE_RATE_KEY = 'framerate.from';
  const hasSourceRate = [...walkFields(tool.options_schema)].some((f) => f.key === SOURCE_RATE_KEY);
  /** The value this page last pre-filled, so a re-fill never overwrites a typed rate. */
  let prefilledRate: OptionValue = null;

  /**
   * "Subtitles were made for" says it is pre-filled from the file when the file
   * declares a rate, and nothing filled it. The framerate op refuses a `from`
   * that disagrees with the document's own rate, so on a MicroDVD file the
   * user had to retype a number the file already stated.
   *
   * Only an empty box, or one this function filled, is written: a rate the
   * user typed is theirs.
   */
  function prefillSourceRate(rate: Framerate): void {
    if (!hasSourceRate) return;
    const current = values[SOURCE_RATE_KEY] ?? null;
    if (current !== null && !sameRate(current, prefilledRate)) return;
    const next: OptionValue = { num: rate.num, den: rate.den };
    prefilledRate = next;
    // Filled from the file, so the refusal message need not wait for it.
    entered.add(SOURCE_RATE_KEY);
    values = { ...values, [SOURCE_RATE_KEY]: next };
    panel?.setValues({ [SOURCE_RATE_KEY]: next });
  }

  /**
   * `countAs` is what the usage counter counts this file under (it counts each object once).
   * The image-subtitle path passes the file the user DROPPED, so the SRT it hands over again
   * after every correction is not counted again as a new upload.
   */
  async function ingest(
    file: File,
    framerate: Framerate | null = null,
    countAs: object = file,
  ): Promise<void> {
    sourceName = file.name;
    let kind = classify(file);

    if (kind === 'audio') {
      // B6. The only place in Path B where free-and-unlimited does not apply.
      void uploads.offer(file);
      return;
    }
    // A NEW FILE SUPERSEDES THE LAST ONE BEFORE IT IS READ, not once it parses.
    // Live, a header-less .sub dropped after an SRT left the SRT's preview on
    // screen and its bytes behind the Download button, so the button handed
    // over the previous file under the new one's error.
    const mine = ++ingestGeneration;
    currentFile = file;
    clearResult();
    // A match made for the last subtitle file is not this one's.
    autosync?.subtitlesChanged();
    if (framerate === null && fpsInput) {
      // A rate typed for the previous file says nothing about this one.
      fpsInput.value = '';
      if (fpsField) fpsField.hidden = true;
    }
    // A Word document or a PDF (SEO_BRIEF_06 §14), told by its bytes whatever it is called, so
    // before the extension's verdict: a .docx is "unknown" to the dropzone and a Word document
    // saved as .zip is an "archive". A video is never one, and its head is not read twice.
    if (kind !== 'container' && kind !== 'other-video') {
      const dropped = await sniffDocument(file).catch(() => null);
      if (mine !== ingestGeneration) return;
      if (dropped !== null) {
        await ingestDocument(file, dropped, mine, countAs);
        return;
      }
      // Named .docx or .pdf and not one: what it is is for its bytes to say, so it is parsed
      // like any file rather than turned away by its name (an SRT saved as .docx is an SRT).
      if (kind === 'unknown' && isDocumentName(file.name)) kind = 'subtitle';
    }
    if (kind === 'other-video') {
      refuseVideo(file);
      return;
    }
    if (kind === 'unknown') {
      usage.failed(file, failedFormat(extensionOf(file.name), null));
      showState(region, 'B5_UNSUPPORTED_BINARY', unreadableKind(file.name));
      return;
    }
    if (kind === 'container') {
      // SEO_BRIEF_04 item 7 (lib/video-flow.ts): the video's subtitle tracks, a picker when
      // there is a choice, and the picked track lands here as a parsed file would. No B7: a
      // video is large by nature, and only its head is read until a track is picked.
      await video.ingest(file, {
        retainBytes: true,
        stale: () => mine !== ingestGeneration,
        land: async (info, picked) => {
          try {
            await adopt(info, picked, info.byteLength, mine);
          } catch (err) {
            if (mine === ingestGeneration) handleWorkerError(err);
          }
        },
      });
      return;
    }
    if (file.size > LARGE_FILE_BYTES) {
      const frag = renderState('B7', {
        filename: shortFilename(file.name),
        size: fileSize(file.size),
      });
      if (frag) region.replaceChildren(frag);
    }
    // Not on a split page: the batch writes one file per entry, so it would
    // hand back every part one in the ZIP and drop every part two. It used to
    // be parsed and refused, under the bitmap-subtitle banner; it says what to
    // do instead now.
    if (kind === 'archive') {
      if (shape.givesParts) {
        usage.failed(file, 'zip');
        showState(region, 'B5_ARCHIVE_HERE', {});
      } else {
        await ingestArchive(file);
      }
      return;
    }

    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      // The buffer TRANSFERS and this view detaches, so anything we want from
      // it is read first. The length comes back on the result for exactly this
      // reason, but the warning above needed it before the send.
      const byteLength = bytes.byteLength;
      const info = await client.request('parse', {
        // C12: no inputId. The worker is the sole id authority and mints one,
        // returning it on the result; correlation until then is reqId.
        inputName: file.name,
        bytes,
        retainRaw: false,
        // Interactive single file: the encoding override has to re-render
        // immediately, and every option change re-runs on a fresh copy read
        // from these bytes, so they are kept.
        retainBytes: true,
        // The answer to the MicroDVD FPS prompt, when this is that re-parse.
        ...(framerate === null ? {} : { framerate }),
      });
      await adopt(info, file, byteLength, mine, countAs);
    } catch (err) {
      if (mine !== ingestGeneration) return;
      if (!asksForRate(err)) usage.failed(countAs, failedFormat(extensionOf(file.name), err));
      handleWorkerError(err);
    } finally {
      drop.setLoaderStatus(null);
    }
  }

  /**
   * A Word document or a PDF: its text is read here, in the browser (lib/documents.ts), and parsed
   * as TXT, so lines with times keep them and prose is timed from reading speed. The worker
   * keeps that TEXT as the file's bytes, so every option change re-reads a few kilobytes of text
   * rather than the document. A scan, a locked or damaged file and an empty document each say
   * which they are; none hands back an empty file.
   */
  async function ingestDocument(
    file: File,
    dropped: DroppedDocument,
    mine: number,
    countAs: object,
  ): Promise<void> {
    if (dropped.kind === 'pdf') {
      // The page's own words (tools/tool.html `template[data-document-labels]`).
      const bank = qs<HTMLTemplateElement>('template[data-document-labels]');
      const label = bank?.content.querySelector('[data-document-label="pdf"]');
      drop.setLoaderStatus(label?.textContent?.trim() || null);
    }
    try {
      let read: DocumentRead;
      try {
        read = await readDocument(dropped, {
          buildId: buildId(),
          cancelled: () => mine !== ingestGeneration,
        });
      } catch (err) {
        // pdf.js did not load (the network), or threw on a file it could not take.
        if (mine !== ingestGeneration) return;
        console.error('[tool] the document could not be read', err);
        usage.failed(countAs, dropped.kind);
        showState(region, 'B5_DOCUMENT_UNREADABLE', { filename: shortFilename(file.name) });
        return;
      }
      if (mine !== ingestGeneration) return;
      if (!read.ok) {
        usage.failed(countAs, dropped.kind);
        if (read.reason === 'scanned') showState(region, 'B5_SCANNED_PDF', {});
        else if (read.reason === 'empty') showState(region, 'B5_EMPTY', {});
        else showState(region, 'B5_DOCUMENT_UNREADABLE', { filename: shortFilename(file.name) });
        return;
      }
      const bytes = new TextEncoder().encode(read.text);
      const info = await client.request('parse', {
        inputName: file.name,
        bytes,
        retainRaw: false,
        retainBytes: true,
        // The text was decoded from the document already; there is no encoding to guess.
        format: 'txt',
        encodingOverride: 'utf-8',
      });
      await adopt(info, file, file.size, mine, countAs, dropped.kind);
    } catch (err) {
      if (mine !== ingestGeneration) return;
      usage.failed(countAs, dropped.kind);
      handleWorkerError(err);
    } finally {
      drop.setLoaderStatus(null);
    }
  }

  /**
   * A parsed file becomes the page's source: shown, counted, and run. A dropped subtitle file
   * arrives here from `parse`, and a video's track from `extractTrack` (lib/video-flow.ts), once
   * per pick, so picking another track replaces the first.
   */
  async function adopt(
    info: ParsedInfo,
    file: File,
    byteLength: number,
    mine: number,
    countAs: object = file,
    shown: DocumentKind | null = null,
  ): Promise<void> {
    if (mine !== ingestGeneration) {
      // Another drop started while this one parsed.
      client.dispose([info.docId], [info.inputId]);
      return;
    }
    source.load(info);
    // A document's text is parsed as TXT, but TXT is not what was dropped: left as the source
    // format, "the same format back" would save /word-to-srt's result as text, not SRT.
    sourceFormat = shown === null ? info.format : undefined;
    sourceRate = info.framerate;
    if (picking) await showSource();
    else showInTable(info.docId, info.cueCount);
    if (mine !== ingestGeneration) return;
    paintParsed(info, file, byteLength, shown);
    // A file with no times in it was timed from reading speed (the TXT reader's loss entry),
    // and the page says so now, before the times are mistaken for real ones. A notice, not a
    // focus-stealing banner: nothing is wrong. The rate is the macro's own default, held equal
    // to the reader's PROFILE_LATIN.targetCps by test/convert/documents.test.ts, so no table of
    // reading speeds rides in every page's bundle for one number.
    if (info.loss.some((l) => l.code === 'timing-interpolated')) {
      const notice = renderState('TIMES_ESTIMATED', {});
      if (notice) region.replaceChildren(notice);
    }
    autosync?.refresh();
    // Counted at the parse, not at each run: an option or encoding change
    // re-runs without coming back here, and an FPS answer that parses this
    // file again is the same `File`, which counts once.
    usage.loaded(countAs, shown ?? info.format);
    track('tool_used', { tool: tool.slug });
    await run();
  }

  /** A video on a merge page: the first text track picked joins the list. */
  async function videoTrackOnce(
    file: File,
    retainBytes: boolean,
    mine: number,
  ): Promise<ExtractedTrackInfo | null> {
    let landed: ExtractedTrackInfo | null = null;
    await video.ingest(file, {
      retainBytes,
      once: true,
      stale: () => mine !== ingestGeneration,
      land: async (info) => {
        landed = info;
      },
    });
    return landed;
  }

  /* ── batch (B4) ─────────────────────────────────────────────────────── */

  async function ingestArchive(file: File): Promise<void> {
    try {
      const ops = opsToApply(page, values);
      // A ZIP is saved in the format picked on the page too, where there is a choice.
      setWriteFormat(chosenFormat ?? writeFormatFor(ops, page.write_formats ?? []));
      const format = writeFormat;
      if (!format) return;
      const bytes = new Uint8Array(await file.arrayBuffer());
      const result = await client.request('batch', {
        items: [{ inputName: file.name, bytes }],
        ops: ops as never,
        format,
        options: writeOptionsFor(format),
      });
      paintBatch(result.items);
      output = { bytes: result.archive, filename: result.archiveName, archive: true };
      if (downloadButton) downloadButton.disabled = false;
      usage.loaded(file, 'zip');
      track('tool_used', { tool: tool.slug });
    } catch (err) {
      usage.failed(file, 'zip');
      handleWorkerError(err);
    }
  }

  /** Reads one of the two labels the template carries for B4. */
  function batchLabel(status: 'converted' | 'skipped'): string {
    // Document-scoped: `root`'s narrowing does not survive into this closure,
    // and the two labels are unique on the page anyway.
    const node = qs<HTMLElement>(`[data-batch-label="${status}"]`);
    return node?.textContent?.trim() ?? status;
  }

  /** One malformed file in forty must not fail the set, and it says which. */
  function paintBatch(items: readonly BatchItemStatus[]): void {
    if (!batchList) return;
    batchList.hidden = false;
    batchList.replaceChildren();
    for (const item of items) {
      const row = document.createElement('li');
      const name = document.createElement('span');
      name.className = 'batch__name';
      name.textContent = shortFilename(item.inputName);
      const status = document.createElement('span');
      status.className = 'batch__status';
      setData(status, 'status', item.status === 'converted' ? 'done' : 'skipped');
      // The label comes out of the template, never out of this file: 'converted'
      // and 'skipped' are machine values on the wire and words on the screen,
      // and the words are the founder's.
      status.textContent = batchLabel(item.status);
      row.append(name, status);
      if (item.reason) {
        const reason = document.createElement('span');
        reason.className = 'batch__reason';
        reason.textContent = item.detail ?? item.reason;
        row.append(reason);
      }
      batchList.append(row);
    }
  }

  /* ── B6, the audio upload ───────────────────────────────────────────── */

  // The offer, the upload and its refusals (lib/upload-offer.ts). An audio page's own format
  // rides along on the job URL, so the transcript page offers it first when the job is done.
  // A language page declares its language with the upload; no other page sends one.
  const uploads = createUploadOffer({
    region,
    tool: tool.slug,
    onSubmitted: (response) => {
      // The transcript is the result, and the job page carries on: leaving is not a no-result.
      resultReached(tool.slug);
      // A translation asked for and already in that language: /t/ says so (`?asked=`).
      const next = response.resolved
        ? withAsked(response.transcript_url, response.translate)
        : response.job_url;
      // The summarizers and the caption generator: the transcript page summarizes it, or writes
      // captions for the platform picked, in the language picked (`?do=`, lib/hand-off.ts).
      // Every other page: unchanged.
      location.assign(
        withThen(
          withDownload(next, page.transcript_format),
          page.then,
          pickedPlatform(root),
          pickedLang(root),
        ),
      );
    },
    ...(page.language ? { language: page.language } : {}),
    // A page that opens on a pair sends the pair's speech language, which its swap can change
    // (SEO_BRIEF_11, /es/traductor-de-audio); every other page has none and sends the above.
    languageNow: () => pairSource(root),
    // A translation page sends its picker's target (SEO_BRIEF_09 2.1); no other page has one.
    translateTo: () => translateTarget(),
    // And the offer names it (Part C): the language picked, not always English.
    translateName: () => translateTargetName(),
  });

  // The pair and its swap (SEO_BRIEF_11 section 3); nothing on a page without one.
  mountTranslatePair(root);

  // /free-transcription (SEO_BRIEF_08 section 3): the microphone panel's Record pane under the
  // drop zone, its Transcribe going down this page's B6 like a dropped audio file.
  const micHost = qs<HTMLElement>('[data-mic-panel]', root);
  if (micHost) {
    mountMicPanel(micHost, {
      transcribe: (file, seconds, counted) => uploads.send(file, seconds, counted),
    });
  }

  /* ── video files (SEO_BRIEF_04 item 7) ───────────────────────────────── */

  // A page whose transcript is text (/video-to-text, /mp4-to-text) says B6_VIDEO's "we'll turn
  // the speech into text" rather than "we'll make subtitles" (`as_text`, states.html).
  // The PDF pages (SEO_BRIEF_08 section 7.5) promise the words too, as a PDF.
  const asText =
    page.input === 'media' &&
    (page.transcript_format === 'txt' || page.transcript_format === 'pdf');
  const video = createVideoFlow({
    client,
    region,
    mode: page.input === 'media' ? 'transcribe' : 'extract',
    status: (message) => drop.setLoaderStatus(message),
    offerUpload: (audio, banner, source) =>
      void uploads.offer(
        audio,
        asText ? { state: banner.state, values: { ...banner.values, as_text: '1' } } : banner,
        source,
      ),
    uploadCap: () => within(loadPlatforms(), 4000, null).then((p) => p?.upload_max_bytes ?? null),
    onError: handleWorkerError,
    refuseVideo,
  });

  /* ── wiring ─────────────────────────────────────────────────────────── */

  /* ── image subtitles (SUP/PGS, VobSub): OCR, SEO_BRIEF_04 item 8 ──────── */

  // The OCR pages read pictures of text with tesseract.js, loaded on the first such drop
  // (lib/image-subtitles.ts). Every other page sends the file there with a link.
  const ocrPage = isOcrPage(page.read_formats);
  const ocrHost = qs<HTMLElement>('[data-ocr-panel]', root);
  let ocrRun: { cancel(): void } | null = null;
  /** Bumped by every drop, so a slow read of one drop's file heads cannot act over a newer one. */
  let dropGeneration = 0;

  /** A drop's files, where they went before image subtitles had pages of their own. */
  function takeFiles(dropped: File[]): void {
    // A merge takes every file it is given, in order. Every other page takes
    // the first: one file in is what it runs on.
    if (shape.takesMany) {
      addFiles(dropped);
      return;
    }
    const first = dropped[0];
    if (first) void ingest(first);
  }

  /**
   * A drop with a .sup, .idx or .sub in it. The .sub is the ambiguous one: VobSub pictures or
   * MicroDVD text, told apart by its first bytes (classify.ts). MicroDVD, and anything else
   * that turns out not to be an image subtitle, goes where it always went.
   */
  async function takeMaybeImageSubtitles(dropped: File[], mine: number): Promise<void> {
    const found: ImageDrop<File> = await readImageDrop(dropped);
    if (mine !== dropGeneration) return;
    if (found.kind === 'none') {
      takeFiles(dropped);
      return;
    }
    ocrRun?.cancel();
    ocrRun = null;
    const first = dropped[0];
    if (!ocrPage || !ocrHost) {
      // Not here: the page that reads it is one link away.
      if (first) usage.failed(first, found.kind === 'pgs' ? 'sup' : 'idx');
      const pgs = found.kind === 'pgs';
      showState(region, 'B5_IMAGE_SUBTITLE', {
        kind: pgs ? t('a SUP/PGS subtitle file') : t('a VobSub (IDX/SUB) subtitle file'),
        page: pgs ? t('SUP to SRT') : t('IDX/SUB to SRT'),
        href: pgs ? OCR_PAGES.pgs : OCR_PAGES.vobsub,
      });
      return;
    }
    if (found.kind === 'needs-idx' || found.kind === 'needs-sub') {
      // Half a VobSub pair.
      if (first) usage.failed(first, 'idx');
      showState(
        region,
        found.kind === 'needs-idx' ? 'B5_VOBSUB_NEEDS_IDX' : 'B5_VOBSUB_NEEDS_SUB',
        {
          filename: shortFilename(found.name),
        },
      );
      return;
    }
    const source = await sourceOf(found);
    if (mine !== dropGeneration) return;
    await startOcr(source, found.name, found.kind === 'pgs' ? found.sup : found.sub);
  }

  /**
   * Recognise an image subtitle, then hand its text to `ingest` as an SRT file, so the preview,
   * the format picker and the download are the converter's own. Each correction the user makes
   * in the check list hands it over again.
   *
   * Counted here, under the picture format it came in (`sup`, or `idx` for VobSub): "loaded" when
   * the text first arrives and "failed" when the pictures cannot be read. `countAs` counts once,
   * so `ingest`'s own count of the SRT it is handed is a no-op.
   */
  async function startOcr(
    source: ImageSubtitleSource,
    name: string,
    countAs: object,
  ): Promise<void> {
    if (!ocrHost) return;
    // A new file supersedes the last result before it is read, as on every drop.
    ingestGeneration += 1;
    currentFile = null;
    clearResult();
    region.replaceChildren();
    drop.setLoaderStatus(t('Getting ready…'));
    let ocr: Awaited<ReturnType<typeof loadOcr>>;
    try {
      ocr = await loadOcr();
    } catch (err) {
      console.error('[tool] the OCR module did not load', err);
      showState(region, 'NETWORK', {});
      return;
    } finally {
      drop.setLoaderStatus(null);
    }
    ocrRun?.cancel();
    const picture = source.kind === 'pgs' ? 'sup' : 'idx';
    ocrRun = ocr.recognise({
      host: ocrHost,
      source,
      name,
      buildId: buildId(),
      onText: (srt, filename) => {
        usage.loaded(countAs, picture);
        void ingest(new File([srt], filename, { type: 'text/plain' }), null, countAs);
      },
      onFailed: () => usage.failed(countAs, picture),
    });
  }

  const drop = mountDropzone(dropHost, {
    onFiles(dropped) {
      // Any new file supersedes a B6 banner still waiting on its numbers, and a
      // video's track list or audio copy still waiting on the last drop.
      uploads.supersede();
      video.abandon();
      region.replaceChildren();
      const mine = ++dropGeneration;
      // /auto-sync-subtitles takes the video (or its audio) and the subtitle
      // file in one drop: the video is what the subtitles are matched to, not
      // a B6 transcription and not a container to read subtitle tracks from.
      let files = dropped;
      if (autosync) {
        const media = dropped.filter((f) => autosync.takes(f));
        const last = media[media.length - 1];
        if (last) autosync.setMedia(last);
        files = dropped.filter((f) => !autosync.takes(f));
      }
      if (mayHoldImageSubtitles(files)) {
        void takeMaybeImageSubtitles(files, mine);
        return;
      }
      if (ocrRun) {
        // A text file dropped on an OCR page replaces the recognition under way.
        ocrRun.cancel();
        ocrRun = null;
        if (ocrHost) ocrHost.hidden = true;
      }
      takeFiles(files);
    },
  });

  // An image track picked from a video (lib/video-flow.ts) goes to OCR: recognised here on an
  // OCR page, sent to /sup-to-srt or /idx-sub-to-srt from every other page.
  registerImageSubtitleHandler(
    ocrHandoverHandler(ocrPage && ocrHost ? (source, name) => startOcr(source, name, {}) : null),
  );

  // Sent here by another page (an image track found inside a video): recognise it as though
  // it had been dropped.
  if (ocrPage) {
    void takeHandedOver().then((handed) => {
      if (handed) void startOcr(handed.source, handed.name, {});
    });
  }

  if (fileReset) on(fileReset, 'click', resetFiles);

  if (formatSelect) {
    // Only the formats this page's result can be saved in (`saveAsChoices`): all of them on
    // a merge, four on the colour page, three on the position page.
    const offered = new Set<string>(saveAsChoices(page.ops ?? [], page.write_formats ?? []));
    for (const option of Array.from(formatSelect.options)) {
      if (!offered.has(option.value)) option.remove();
    }
    // A merge shows the choice once a file is in (`paintFiles`); a one-file page shows it
    // beside its Download button from the start, so the format can be picked before the file.
    if (formatField && !shape.takesMany) formatField.hidden = false;
    on(formatSelect, 'change', () => {
      // The user's choice, kept over the first file's (or the row's first) from
      // here on. The run writes it; nothing else about the result changes.
      chosenFormat = formatSelect.value as WriteFormat;
      // The button says so now, not at the next run: on a one-file page the
      // choice can be made before there is a file to run on.
      setWriteFormat(chosenFormat);
      void run();
    });
  }

  // Which boxes the user has filled, and whether they are mid-edit, for the
  // refusal message. Delegated, because the panel builds its controls at
  // runtime; each control's own listener runs first and has already started
  // the run by the time these see the event.
  const fieldKeyOf = (target: EventTarget | null): string | null =>
    target instanceof Element
      ? (target.closest('[data-field]')?.getAttribute('data-field') ?? null)
      : null;
  on(optionsHost, 'input', (ev) => {
    const key = fieldKeyOf(ev.target);
    if (key !== null) entered.add(key);
    // Typing is input in the box that has focus. A preset chip writes its box
    // and keeps focus on itself, and that is a finished answer.
    typing = ev.target === document.activeElement;
    paintRefusal();
  });
  on(optionsHost, 'change', (ev) => {
    const key = fieldKeyOf(ev.target);
    if (key !== null) entered.add(key);
    typing = false;
    paintRefusal();
  });
  on(optionsHost, 'focusout', () => {
    typing = false;
    paintRefusal();
  });
  // For a pick off the table: the box the user is in, or was in last.
  on(optionsHost, 'focusin', (ev) => {
    lastFocusedKey = fieldKeyOf(ev.target);
  });

  if (encodingSelect) {
    on(encodingSelect, 'change', () => {
      if (source.inputId === null) return;
      // The next fresh copy decodes the retained bytes with this label; the
      // run makes it, so a change costs one re-read rather than two.
      source.setEncoding(encodingSelect.value);
      // The panel's summary names the encoding the file is read as. It said
      // the first guess after a change, so "Read as utf-8" sat above a select
      // set to Windows-1252 and a preview that read correctly.
      const named = previewHost ? qs<HTMLElement>('[data-slot="encoding"]', previewHost) : null;
      if (named) named.textContent = encodingSelect.value;
      // The table's own copy of the file went with the old label.
      if (picking) void showSource().catch(handleWorkerError);
      void run();
    });
  }

  if (fpsInput) {
    // THE MICRODVD FPS PROMPT HAD NO LISTENER. The box was revealed and
    // focused when a parse asked for a rate, and typing into it did nothing:
    // the page kept the previous file and Download returned it. It now parses
    // the same file again with the rate typed, which is what
    // `ParseMessage.framerate` exists for.
    let lastAsked = '';
    const answer = () => {
      const typed = fpsInput.value.trim();
      const rate = labelled(parseFramerate(typed));
      fpsInput.setAttribute('aria-invalid', rate === null && typed !== '' ? 'true' : 'false');
      if (rate === null || currentFile === null) return;
      // `change` and Enter both fire for one answer; parse once.
      const asked = `${currentFile.name}:${currentFile.size}:${rate.num}/${rate.den}`;
      if (asked === lastAsked) return;
      lastAsked = asked;
      region.replaceChildren();
      // On a merge the file joins the list with its rate; anywhere else it
      // replaces the file that asked.
      if (shape.takesMany) addFiles([currentFile], rate);
      else void ingest(currentFile, rate);
    };
    on(fpsInput, 'change', answer);
    on(fpsInput, 'keydown', (ev) => {
      if ((ev as KeyboardEvent).key !== 'Enter') return;
      ev.preventDefault();
      answer();
    });
    // A new value is a new question, even for the same file.
    on(fpsInput, 'input', () => {
      lastAsked = '';
    });
  }

  /**
   * Hand over one result, and count it in the format and encoding it is saved
   * in. A batch's result is the ZIP the converted files came back in.
   */
  function handOver(out: { bytes: Uint8Array | string; filename: string; archive?: boolean }) {
    const format = writeFormat ?? 'srt';
    const encoding = saveEncoding.encodingFor(writeFormat);
    const filename = out.archive ? out.filename : ttmlExtension.filenameFor(format, out.filename);
    saveBytes(out.bytes, filename, format, encoding);
    if (out.archive) usage.downloaded('zip');
    else usage.downloaded(format, encoding);
    track('format_download', { format });
  }

  if (downloadButton) {
    on(downloadButton, 'click', () => {
      // Synchronous from here to the anchor click: the bytes were produced
      // before the button was enabled precisely so this handler can be.
      if (output === null) return;
      handOver(output);
    });
  }

  if (secondButton) {
    on(secondButton, 'click', () => {
      // Synchronous, for the same reason as the first button.
      if (secondOutput === null) return;
      handOver(secondOutput);
    });
  }

  on(region, 'click', (ev) => {
    const target = ev.target;
    if (target instanceof HTMLElement && target.getAttribute('data-action') === 'reload') {
      location.reload();
    }
  });

  addEventListener('pagehide', () => {
    panel?.destroy();
    table?.destroy();
    diff?.destroy();
    drop.destroy();
    autosync?.destroy();
    const held = source.held();
    client.dispose(held.docIds, held.inputIds);
  });

  void sourceName;
}
